import React from 'react';
import { useLanguage } from '../../locale/useLanguage';
import {
  AttemptState,
  CalculatedOrderStatus,
  Delay,
  Problem,
} from '../../models';
import { Button } from '../Button/Button';
import './BackCard.css';
import noProfileImage from '../../assets/images/no-profile-picture.png';
import {
  getDetailsHeadingByOrderStateAndAttempt,
  getFirstDescription,
  getSecondDescription,
} from './backCardHelpers';
import { NormalizedRange } from '../../helpers/dateTimes';
import { useIsEditAddressEnabled } from '../../hooks/mlkActions/useIsEditAddressEnabled';
import { useIsEditNotesEnabled } from '../../hooks/mlkActions/useIsEditNotesEnabled';

export type BackCardProps = {
  attempt: AttemptState;
  orderState: CalculatedOrderStatus;
  expectedServiceDateAndTimeSlot?: NormalizedRange | null;
  isLastAttempt?: boolean;
  instructions?: string;
  noteForCustomer?: string;
  problem?: Problem;
  pinCode: string;
  trackingNumber: string;
  deliveryAddress: string;
  intercomName: string;
  driverName?: string;
  driverImage?: string;
  poName?: string;
  withdrawHour?: string;
  withdrawDate?: string;
  delay?: Delay;
  completeName?: string;
  merchantName?: string;
  isServiceAttemptExist?: boolean;
  onEditAddressClick?: () => void;
  onEditIntercomName?: () => void;
  onVerifyDelivery?: () => void;
};

export const BackCard: React.FC<BackCardProps> = ({
  attempt,
  problem,
  deliveryAddress,
  driverName,
  isLastAttempt,
  driverImage,
  intercomName,
  instructions,
  orderState,
  pinCode,
  trackingNumber,
  noteForCustomer,
  withdrawHour,
  withdrawDate,
  delay,
  poName,
  merchantName,
  isServiceAttemptExist,
  onEditAddressClick,
  onEditIntercomName,
  onVerifyDelivery,
  expectedServiceDateAndTimeSlot,
  completeName,
}) => {
  const [t] = useLanguage();

  const isEditAddressEnabled = useIsEditAddressEnabled();
  const isEditIntercomEnabled = useIsEditNotesEnabled();

  const isDeliveryInStock = Boolean(poName && withdrawDate && withdrawHour);

  const heading = getDetailsHeadingByOrderStateAndAttempt({
    isLastAttempt,
    isInPo: isDeliveryInStock,
    orderState,
    attempt,
    problem,
    arrivingDate: expectedServiceDateAndTimeSlot?.from,
    t,
  });

  const firstDescription = getFirstDescription({
    problem,
    orderState,
    delay,
    merchantName,
    arrivingDate: expectedServiceDateAndTimeSlot?.from,
    t,
  });
  const secondDesription = getSecondDescription({
    isLastAttempt,
    orderState,
    delay,
    problem,
    attempt,
    t,
    options: isDeliveryInStock
      ? {
          hour: withdrawHour,
          date: withdrawDate,
          poName,
        }
      : undefined,
  });

  return (
    <div className="back-card--details-container">
      <h2 className="back-card--heading">{heading}</h2>
      <p className="back-card--text-details">{firstDescription}</p>
      {orderState !== CalculatedOrderStatus.Fulfilled && (
        <p className="back-card--text-details">{secondDesription}</p>
      )}
      <div className="back-card--separator" />
      <div className="back-card--section">
        <h3 className="back-card--sub-heading">{t('homeDetails.pinCode')}</h3>
        <p className="back-card--text-details">{pinCode}</p>
      </div>
      <div className="back-card--section">
        <h3 className="back-card--sub-heading">
          {t('homeDetails.trackingNumber')}
        </h3>
        <p className="back-card--text-details back-card--copy-text">
          {trackingNumber}
        </p>
      </div>
      {completeName && (
        <div className="back-card--section">
          <h3 className="back-card--sub-heading">
            {t('homeDetails.completeName')}
          </h3>
          <p className="back-card--text-details back-card--copy-text">
            {completeName}
          </p>
        </div>
      )}
      <div className="back-card--section">
        <h3 className="back-card--sub-heading">
          {t('homeDetails.deliveryAddress')}
        </h3>
        <p className="back-card--text-details">{deliveryAddress}</p>
        {isEditAddressEnabled && (
          <a
            className="link"
            onClick={onEditAddressClick}
            data-testid="back-card--edit-address-link"
          >
            {t('homeDetails.edit')}
          </a>
        )}
      </div>
      {intercomName && (
        <div className="back-card--section">
          <h3 className="back-card--sub-heading">
            {t('homeDetails.intercom')}
          </h3>
          <p className="back-card--text-details">{intercomName}</p>
          {isEditIntercomEnabled && (
            <a
              className="link"
              onClick={onEditIntercomName}
              data-testid="back-card--edit-intercom-name-link"
            >
              {t('homeDetails.edit')}
            </a>
          )}
        </div>
      )}
      {instructions && (
        <div className="back-card--section">
          <h3 className="back-card--sub-heading">
            {t('homeDetails.instructions')}
          </h3>
          <p className="back-card--text-details">{instructions}</p>
          {isEditIntercomEnabled && (
            <a
              className="link"
              onClick={onEditIntercomName}
              data-testid="back-card--edit-intercom-name-link"
            >
              {t('homeDetails.edit')}
            </a>
          )}
        </div>
      )}
      {noteForCustomer && (
        <div className="back-card--section">
          <h3 className="back-card--sub-heading">
            {t('homeDetails.courierNotes')}
          </h3>
          <p className="back-card--text-details">{noteForCustomer}</p>
        </div>
      )}
      {driverName && (
        <div className="back-card--section back-card--profile-section">
          <div>
            <h3 className="back-card--sub-heading">
              {t('homeDetails.yourDriver')}
            </h3>
            <p className="back-card--text-details">{driverName}</p>
          </div>
          <img
            className="back-card--driver-image"
            src={driverImage || noProfileImage}
            alt="Driver image"
          />
        </div>
      )}
      {isServiceAttemptExist && (
        <div className="back-card--cta-container">
          <Button
            onClick={onVerifyDelivery}
            isPrimary
            label={t('homeDetails.verifyDelivery')}
          />
        </div>
      )}
    </div>
  );
};
